import { DashboardState, ESdChangeReason } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { isMcpChatBusy, subscribeMcpChatBusy } from "../../left-menu/mcp-chat/panel/mcp-chat-busy";
import { isMcpChatPanelOpen, toggleMcpChatPanel } from "../../left-menu/mcp-chat/panel/open-mcp-chat-panel";
import "./ai-chat-button.scss";

export interface IAiChatButtonProps {}

/**
 * "spinning" — from first paint, for as long as the project is still loading.
 * "settling" — the loader has cleared; the ring holds `$ai-ring-hold` (10s), then fades.
 * "done" — the fade landed; both ring classes come off so nothing animates behind opacity 0.
 */
type RingPhase = "spinning" | "settling" | "done";

/** Must match the settle keyframe name in ai-chat-button.scss. */
const RING_SETTLE_ANIMATION_NAME: string = "kuika-ai-ring-settle";

export interface IAiChatButtonState {
  ringPhase: RingPhase;
  /** A chat turn is running in some tab — the ring spins for as long as it does. */
  isAiWorking: boolean;
}

/**
 * Top-bar AI trigger — the single entry point to the Kuika AI chat, sitting left of Preview.
 *
 * Replaces the draggable bottom-right FAB: the AI is a first-class tool, so it belongs in the
 * action row with Preview and Issues rather than floating over the canvas the user is editing.
 *
 * The square wears a rotating ring (the same `--ai-ring-*` ramp the canvas "AI is working" glow
 * uses) from first paint, through the whole project load, and for 10s after the loader clears —
 * then it fades out and the button settles onto its plain neutral border. A one-time "the AI
 * lives here" cue, not a permanent animation competing with the canvas. The hold is keyed on
 * `sd.isProjectLoaded`, not on a fixed duration from mount: the toolbar paints BEHIND the
 * full-screen loader, so a mount-timed countdown expires while the user is still watching the
 * spinner. See `RingPhase` and ai-chat-button.scss.
 *
 * The same ring comes back whenever the AI is working — any chat tab with a turn running
 * (`mcp-chat-busy.ts`), chat open or closed — and stops when the turn ends or pauses to ask the
 * user something.
 *
 * Toggles: a second click dismisses the chat. While it is showing the square wears a
 * `--purple-6` frame matching its glyph. Both need to know whether the chat is on screen, which
 * is why floating visibility was lifted out of DesignLeftMenu onto the SD context — see
 * `open-mcp-chat-panel.ts` for the docked-vs-floating shapes of "open".
 */
class AiChatButton extends PureSdComponent<IAiChatButtonProps & IWithSDProps, IAiChatButtonState> {
  constructor(props: IAiChatButtonProps & IWithSDProps) {
    super(props);
    this.state = { ringPhase: "spinning", isAiWorking: isMcpChatBusy() };
  }

  private unsubscribeBusy: (() => void) | undefined;

  componentDidMount(): void {
    this.startSettlingWhenProjectLoaded();
    this.unsubscribeBusy = subscribeMcpChatBusy((busy: boolean): void => this.setState({ isAiWorking: busy }));
    // A turn may have started between the constructor's read and the subscription.
    if (isMcpChatBusy() !== this.state.isAiWorking) this.setState({ isAiWorking: isMcpChatBusy() });
  }

  componentWillUnmount(): void {
    this.unsubscribeBusy?.();
  }

  componentDidUpdate(): void {
    this.startSettlingWhenProjectLoaded();
  }

  private startSettlingWhenProjectLoaded = (): void => {
    if (this.state.ringPhase !== "spinning") return;
    if (this.props.sd.isProjectLoaded !== true) return;
    this.setState({ ringPhase: "settling" });
  };

  // CSS animations on a pseudo-element bubble their end event to the originating element, so the
  // wrapper is where the fade reports in. Guarded on the name — the spin leg never ends.
  private handleRingAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>): void => {
    if (e.animationName !== RING_SETTLE_ANIMATION_NAME) return;
    this.setState({ ringPhase: "done" });
  };

  private handleClick = (): void => {
    toggleMcpChatPanel(this.props.sd);
  };

  render(): React.ReactElement | null {
    // The chat is deliberately unavailable in the report designer — DesignLeftMenu cancels its
    // active requests and force-closes it on entry, and the FAB this replaced was hidden there.
    // The toolbar renders in that state (Save is report-designer-only), so without this gate the
    // trigger would hand back the one surface the mode is supposed to withhold.
    if (this.props.sd.dashboardState === DashboardState.reportDesigner) return null;

    const isOpen: boolean = isMcpChatPanelOpen(this.props.sd);
    // Deliberately NOT KuikaButton's `active`: the neutral variant's active state paints a
    // --blue-4 frame and a --dark-8 glyph, which would drop the AI accent this control is
    // identified by. Own modifier, purple frame, glyph untouched.
    const triggerClassName: string = `kuika-ai-chat-button__trigger${
      isOpen ? " kuika-ai-chat-button__trigger--open" : ""
    }`;
    // While the AI works the ring spins, whatever the load-time flourish is doing; the flourish's
    // own classes stay so its settle still runs to the end underneath.
    const ringModifier: string = `${
      {
        spinning: " kuika-ai-chat-button--ring",
        settling: " kuika-ai-chat-button--ring-settling",
        done: ""
      }[this.state.ringPhase]
    }${this.state.isAiWorking ? " kuika-ai-chat-button--working" : ""}`;
    return (
      <div className={`kuika-ai-chat-button${ringModifier}`} onAnimationEnd={this.handleRingAnimationEnd}>
        <KuikaButton
          data-testid="toolbarAiChatButton"
          data-onboarding-id="header_toolbar-right_ai-chat-button"
          type="secondary"
          color="neutral"
          size="md"
          isIcon
          className={triggerClassName}
          aria-expanded={isOpen}
          // Accessible name kept verbatim from the FAB this replaced — it is what the
          // assistive-tech and E2E locators already address.
          aria-label="Open AI Chat"
          onClick={this.handleClick}
        >
          <div className="kuika-flex-center" title={this.state.isAiWorking ? "Kuika AI is working" : "Kuika AI"}>
            <KuikaSvg name="sparkles" type="datasource-icons" width={18} height={18} />
          </div>
        </KuikaButton>
      </div>
    );
  }

  // Always re-render on an sd change: "is the chat open" is derived from three moving parts
  // (isMcpChatVisible, chatDockedToPanel, the active right-panel tab), and a targeted compare
  // would silently miss one. The cost is one 32px button.
  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const aiChatButton = withSD<IAiChatButtonProps>(AiChatButton);
export { aiChatButton as AiChatButton };
